/**
 * Modulo di validazione file
 * Centralizza tutte le logiche di validazione per upload file
 * 
 * @module fileValidation
 * @author GitHub Copilot
 * @version 1.0.0
 */

import $ from "jquery";
import { t } from "~/hooks/useI18n";

/**
 * ✅ Estensioni file consentite
 * Modificare questo array per aggiungere/rimuovere estensioni
 */
export const ALLOWED_FILE_EXTENSIONS = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.p7m'
] as const;

/**
 * ✅ Dimensione massima file di default (4 MB)
 */
export const DEFAULT_MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB in bytes

/**
 * Risultato della validazione file
 */
export interface FileValidationResult {
    /** True se il file è valido */
    isValid: boolean;
    /** Messaggio di errore se non valido */
    errorMessage?: string;
    /** Codice errore per gestione programmatica */
    errorCode?: 'INVALID_EXTENSION' | 'FILE_TOO_LARGE' | 'EMPTY_FILE';
}

/**
 * ✅ Valida l'estensione del file
 * @param fileName Nome del file da validare
 * @returns true se l'estensione è consentita, false altrimenti
 * 
 * @example
 * ```typescript
 * validateFileExtension("documento.pdf"); // true
 * validateFileExtension("virus.exe");     // false
 * ```
 */
export function validateFileExtension(fileName: string): boolean {
    if (!fileName) return false;

    const lowerFileName = fileName.toLowerCase();
    const extension = lowerFileName.substring(lowerFileName.lastIndexOf('.'));

    return ALLOWED_FILE_EXTENSIONS.includes(extension as any);
}

/**
 * ✅ Ottiene l'estensione del file (lowercase)
 * @param fileName Nome del file
 * @returns L'estensione del file (es. ".pdf")
 * 
 * @example
 * ```typescript
 * getFileExtension("Documento.PDF"); // ".pdf"
 * getFileExtension("foto.JPG");      // ".jpg"
 * ```
 */
export function getFileExtension(fileName: string): string {
    if (!fileName) return "";

    const lowerFileName = fileName.toLowerCase();
    return lowerFileName.substring(lowerFileName.lastIndexOf('.'));
}

/**
 * ✅ Valida le dimensioni del file
 * @param fileSize Dimensione del file in bytes
 * @param maxSize Dimensione massima consentita in bytes (default: 4 MB)
 * @returns true se la dimensione è valida, false altrimenti
 * 
 * @example
 * ```typescript
 * validateFileSize(1024 * 1024);        // true (1 MB)
 * validateFileSize(10 * 1024 * 1024);   // false (10 MB > 4 MB)
 * ```
 */
export function validateFileSize(fileSize: number, maxSize: number = DEFAULT_MAX_FILE_SIZE): boolean {
    return fileSize > 0 && fileSize <= maxSize;
}

/**
 * ✅ Valida un file (estensione + dimensione)
 * @param file File da validare
 * @param maxSize Dimensione massima in bytes (default: 4 MB)
 * @returns Risultato della validazione con dettagli errore
 * 
 * @example
 * ```typescript
 * const result = validateFile(file);
 * if (!result.isValid) {
 *     console.error(result.errorMessage);
 * }
 * ```
 */
export function validateFile(file: File, maxSize: number = DEFAULT_MAX_FILE_SIZE): FileValidationResult {
    // Controllo file vuoto
    if (!file || file.size === 0) {
        return {
            isValid: false,
            errorCode: 'EMPTY_FILE',
            errorMessage: t("messages.emptyFile")
        };
    }

    // Validazione estensione
    if (!validateFileExtension(file.name)) {
        const extension = getFileExtension(file.name);
        return {
            isValid: false,
            errorCode: 'INVALID_EXTENSION',
            errorMessage: t("messages.invalidFileExtension", { 
                extension, 
                allowed: ALLOWED_FILE_EXTENSIONS.join(', ') 
            })
        };
    }

    // Validazione dimensione
    if (!validateFileSize(file.size, maxSize)) {
        return {
            isValid: false,
            errorCode: 'FILE_TOO_LARGE',
            errorMessage: t("messages.fileTooLarge", { 
                size: formatFileSize(file.size),
                maxSize: formatFileSize(maxSize)
            })
        };
    }

    // File valido
    return {
        isValid: true
    };
}

/**
 * ✅ Formatta la dimensione del file in formato leggibile
 * @param bytes Dimensione in bytes
 * @returns Stringa formattata (es. "1.5 MB")
 * 
 * @example
 * ```typescript
 * formatFileSize(1024);              // "1 KB"
 * formatFileSize(1536 * 1024);       // "1.5 MB"
 * formatFileSize(5 * 1024 * 1024);   // "5 MB"
 * ```
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * ✅ Mostra un messaggio di errore sotto l'input file
 * @param fileInput Elemento HTMLInputElement del file
 * @param message Messaggio di errore da visualizzare
 * 
 * @example
 * ```typescript
 * showFileError(fileInput, "File troppo grande");
 * ```
 */
export function showFileError(fileInput: HTMLInputElement, message: string): void {
    // Aggiungi classe di errore all'input
    $(fileInput).addClass("is-invalid");

    // Cerca se esiste già un elemento per l'errore
    let errorElement = $(fileInput).siblings(".invalid-feedback");

    if (errorElement.length === 0) {
        // Crea l'elemento per l'errore se non esiste
        $(fileInput).after(`<div class="invalid-feedback d-block">${message}</div>`);
    } else {
        // Aggiorna il messaggio esistente
        errorElement.text(message).removeClass("d-none").addClass("d-block");
    }
}

/**
 * ✅ Rimuove il messaggio di errore dall'input file
 * @param fileInput Elemento HTMLInputElement del file
 * 
 * @example
 * ```typescript
 * clearFileError(fileInput);
 * ```
 */
export function clearFileError(fileInput: HTMLInputElement): void {
    $(fileInput).removeClass("is-invalid");
    $(fileInput).siblings(".invalid-feedback").addClass("d-none").removeClass("d-block");
}

/**
 * ✅ Valida file e mostra errore automaticamente se non valido
 * @param fileInput Elemento HTMLInputElement del file
 * @param file File da validare
 * @param maxSize Dimensione massima in bytes (default: 4 MB)
 * @returns true se il file è valido, false altrimenti
 * 
 * @example
 * ```typescript
 * $("#myFileInput").on("change", function() {
 *     const fileInput = this as HTMLInputElement;
 *     if (fileInput.files && fileInput.files.length > 0) {
 *         if (validateAndShowFileError(fileInput, fileInput.files[0])) {
 *             // File valido, procedi con upload
 *         }
 *     }
 * });
 * ```
 */
export function validateAndShowFileError(
    fileInput: HTMLInputElement, 
    file: File, 
    maxSize: number = DEFAULT_MAX_FILE_SIZE
): boolean {
    // Valida il file
    const result = validateFile(file, maxSize);

    if (!result.isValid) {
        // Mostra errore
        showFileError(fileInput, result.errorMessage || t("messages.invalidFile"));
        fileInput.value = "";
        return false;
    }

    // File valido - rimuovi errori e mostra conferma in console
    clearFileError(fileInput);
    console.info(`✅ File valido: ${file.name} (${formatFileSize(file.size)})`);

    return true;
}

/**
 * ✅ Mostra l'errore personalizzato con UI custom
 * (compatibile con divUploadFile / divUploadFileError)
 * @param fileInput Elemento HTMLInputElement del file
 * @param message Messaggio di errore
 * 
 * @example
 * ```typescript
 * showCustomFileError(fileInput, "Errore: file non valido");
 * ```
 */
export function showCustomFileError(fileInput: HTMLInputElement, message: string): void {
    const $input = $(fileInput);

    // Trova il div divUploadFile più vicino
    const $uploadDiv = $input.closest('.divUploadFile');
    if ($uploadDiv.length > 0) {
        $uploadDiv.addClass('border-primary');
    }

    // Trova il div divUploadFileError successivo
    const $errorDiv = $uploadDiv.parent().find('.divUploadFileError');
    if ($errorDiv.length > 0) {
        $errorDiv.removeClass('d-none').text(message);
    }

    // Aggiungi anche la classe is-invalid per compatibilità
    $input.addClass('is-invalid');
}

/**
 * ✅ Rimuove l'errore personalizzato con UI custom
 * @param fileInput Elemento HTMLInputElement del file
 * 
 * @example
 * ```typescript
 * clearCustomFileError(fileInput);
 * ```
 */
export function clearCustomFileError(fileInput: HTMLInputElement): void {
    const $input = $(fileInput);

    // Rimuovi il bordo rosso dal div divUploadFile
    const $uploadDiv = $input.closest('.divUploadFile');
    if ($uploadDiv.length > 0) {
        $uploadDiv.removeClass('border-primary');
    }

    // Nascondi il messaggio di errore
    const $errorDiv = $uploadDiv.parent().find('.divUploadFileError');
    if ($errorDiv.length > 0) {
        $errorDiv.addClass('d-none');
    }

    // Rimuovi la classe is-invalid
    $input.removeClass('is-invalid');
}

/**
 * ✅ Valida file e mostra bordo rosso su div contenitore mascherato
 * Specifico per input file nascosti con label custom e div divUploadFile
 * 
 * @param fileInput Elemento input file
 * @param file File da validare
 * @param fileNameDisplayId ID del div dove mostrare il nome del file (es. "filePiaName")
 * @param maxSize Dimensione massima in bytes (default: 4 MB)
 * @returns true se il file è valido, false altrimenti
 * 
 * @example
 * ```typescript
 * $("#OffertaFilePia").on("change", function() {
 *     const fileInput = this as HTMLInputElement;
 *     if (fileInput.files && fileInput.files.length > 0) {
 *         validateMaskedFileInputWithBorder(
 *             fileInput, 
 *             fileInput.files[0], 
 *             "filePiaName"
 *         );
 *     }
 * });
 * ```
 */
export function validateMaskedFileInputWithBorder(
    fileInput: HTMLInputElement,
    file: File,
    fileNameDisplayId: string,
    maxSize: number = DEFAULT_MAX_FILE_SIZE
): boolean {
    const $input = $(fileInput);
    const $uploadDiv = $input.closest('.divUploadFile');
    const $errorDiv = $uploadDiv.parent().find('.divUploadFileError');
    const $fileNameDisplay = $(`#${fileNameDisplayId}`);

    // ✅ Valida il file
    const validation = validateFile(file, maxSize);

    if (!validation.isValid) {
        // ❌ File non valido: mostra bordo rosso e messaggio di errore

        // Aggiungi bordo rosso al div
        $uploadDiv.removeClass('border-primary').addClass('border-danger');

        // Mostra messaggio di errore
        if ($errorDiv.length > 0) {
            $errorDiv.removeClass('d-none').text(validation.errorMessage || t("messages.invalidFile"));
        }

        // Nascondi il nome del file
        $fileNameDisplay.html('');

        // Reset input
        $input.val('');

        return false;
    }

    // ✅ File valido: rimuovi bordo rosso e mostra nome file
    $uploadDiv.removeClass('border-danger border-primary');

    // Nascondi messaggio di errore
    if ($errorDiv.length > 0) {
        $errorDiv.addClass('d-none');
    }

    // Mostra il nome del file con icona check
    $fileNameDisplay.html(
        `<span class="material-symbols-outlined md-18">check_circle</span>${file.name}`
    );

    return true;
}

/**
 * ✅ Helper per configurare la validazione automatica su un input file
 * @param selector Selettore jQuery dell'input file
 * @param maxSize Dimensione massima in bytes (default: 4 MB)
 * @param onValidFile Callback opzionale quando il file è valido
 * 
 * @example
 * ```typescript
 * // Configurazione semplice
 * setupFileValidation("#myFileInput");
 * 
 * // Con callback personalizzato
 * setupFileValidation("#myFileInput", 4 * 1024 * 1024, (file) => {
 *     console.log("File valido:", file.name);
 *     // Upload file...
 * });
 * ```
 */
export function setupFileValidation(
    selector: string, 
    maxSize: number = DEFAULT_MAX_FILE_SIZE,
    onValidFile?: (file: File) => void
): void {
    $(selector).on("change", function() {
        const fileInput = this as HTMLInputElement;

        if (fileInput.files && fileInput.files.length > 0) {
            const file = fileInput.files[0];

            if (validateAndShowFileError(fileInput, file, maxSize)) {
                // File valido
                if (onValidFile) {
                    onValidFile(file);
                }
            }
        }
    });
}
